import React from "react";
import PublicHeader from "./PublicHeader";
import PublicFooter from "./PublicFooter";
import PartnerSchools from "./PartnerSchools";
import UpcomingWebinars from "./UpcomingWebinars";
import PartnershipsBar from "./PartnershipsBar";
import YouTubeFeed from "./YouTubeFeed";
import revPrepBanner from "../images/landing-page/revolution-prep-banner.png";
import empowerIcon from "../images/landing-page/icons/Arrow.svg";
import nilIcon from "../images/landing-page/icons/NIL.svg";
import connectivityIcon from "../images/landing-page/icons/Gameplan.svg";
import milestoneIcon from "../images/landing-page/icons/Tracker.svg";
import expertsIcon from "../images/landing-page/icons/Connect.svg";
import ecosystemIcon from "../images/landing-page/icons/HUB.svg";
import "../styles/landing-page.css";

const LANDING_HERO_SRC = `${process.env.PUBLIC_URL || ""}/landing-hero.jpg`;

type Feature = {
    title: string;
    description: string;
    icon: string;
};

const FEATURES: Feature[] = [
    {
        title: "Empower Your Counseling Team",
        description:
            "Give counselors the tools, resources, and insights to guide every student-athlete through the college journey with confidence.",
        icon: empowerIcon,
    },
    {
        title: "Elevate Your NIL Education",
        description:
            "Deliver relevant, engaging NIL education that helps student-athletes understand their opportunities, responsibilities, and value.",
        icon: nilIcon,
    },
    {
        title: "Next-Gen Connectivity",
        description:
            "Create stronger connections between student-athletes and the people, resources, and opportunities that can move their journey forward.",
        icon: connectivityIcon,
    },
    {
        title: "Track Every Milestone",
        description:
            "Keep student-athletes on track with greater visibility into key milestones, action items, and progress throughout their journey.",
        icon: milestoneIcon,
    },
    {
        title: "Connect Athletes With Experts",
        description:
            "Give student-athletes access to trusted experts and real-world perspectives across college, NIL, career, and the broader athlete experience.",
        icon: expertsIcon,
    },
    {
        title: "Connect Your Entire Ecosystem",
        description:
            "Bring student-athletes, families, counselors, coaches, and partners together in one connected platform built around student-athlete success.",
        icon: ecosystemIcon,
    },
];

export function LandingPage() {
    return (
        <main className="landing-page">

            {/* ── Header ── */}
            <PublicHeader />

            {/* ── Hero ── */}
            <section className="landing-hero" style={{ backgroundImage: `url(${LANDING_HERO_SRC})` }}>
                <div className="landing-hero-inner">
                    <div className="landing-hero-content-box">
                        <div className="landing-hero-heading-box">
                            <h1 className="landing-hero-heading">
                                College<br />Counseling x<br />NIL Education.<br />Connected.
                            </h1>
                        </div>
                        <div className="landing-hero-sub-box">
                            <p className="landing-hero-sub">A Platform Built For<br />Student-Athlete Success</p>
                        </div>
                    </div>
                </div>
                <p className="landing-hero-athlete-caption">
                    Polaris Athlete Mentor, Latasha Smith, Track &amp; Field
                </p>
            </section>

            {/* ── Trusted By Our Partner Schools (CMS-driven) ── */}
            <PartnerSchools />

            {/* ── Upcoming Webinars (CMS-driven) ── */}
            <UpcomingWebinars />

            {/* ── Partnerships ── */}
            <PartnershipsBar />

            {/* ── Features ── */}
            <section id="features" className="landing-features">
                <div className="landing-features-header">
                    <p className="landing-section-label landing-section-label--dark">
                        <span className="landing-section-label-diamond">✦</span> Features
                    </p>
                    <h2 className="landing-features-heading">
                        The All-In-One Platform For Student-Athlete Success
                    </h2>
                    <p className="landing-features-subheading">
                        Everything your organization needs to educate, support, and connect
                        student-athletes in today&apos;s evolving landscape.
                    </p>
                </div>
                <div className="landing-features-grid">
                    {FEATURES.map((feature) => (
                        <article key={feature.title} className="landing-feature-card">
                            <img className="landing-feature-card-icon" src={feature.icon} alt="" aria-hidden="true" />
                            <h3 className="landing-feature-card-title">{feature.title}</h3>
                            <p className="landing-feature-card-desc">{feature.description}</p>
                        </article>
                    ))}
                </div>
            </section>

            {/* ── YouTube Feed ── */}
            <YouTubeFeed />

            {/* ── Designed For The Long Game ── */}
            <section id="families" className="landing-longgame">
                <div className="landing-longgame-overlay" />
                <div className="landing-longgame-inner">
                    <p className="landing-section-label">Polaris Athlete</p>
                    <h2 className="landing-longgame-heading">
                        Designed For The Long Game
                    </h2>
                    <p className="landing-longgame-desc">
                        We build platforms and programs that serve student-athletes well beyond their
                        playing days — preparing them for college, career, and life.
                    </p>
                    <div className="landing-longgame-connectors">
                        <div className="landing-connector-item">
                            <span className="landing-connector-dot" />
                            <span>College Counseling</span>
                        </div>
                        <div className="landing-connector-line" />
                        <div className="landing-connector-item">
                            <span className="landing-connector-dot" />
                            <span>NIL Education</span>
                        </div>
                        <div className="landing-connector-line" />
                        <div className="landing-connector-item">
                            <span className="landing-connector-dot" />
                            <span>Career Readiness</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Revolution Prep Banner ── */}
            <section className="landing-revprep">
                <div className="landing-revprep-inner">
                    <div className="landing-revprep-banner">
                        <img src={revPrepBanner} alt="Revolution Prep" className="landing-revprep-banner-logo" />
                        <div className="landing-revprep-banner-content">
                            <h3 className="landing-revprep-banner-heading">Free Test Prep &amp; Tutoring</h3>
                            <p className="landing-revprep-banner-desc">
                                All student-athletes receive 2 hours ($200+ value) of complimentary test prep
                                or tutoring through our partner, RevPrep, helping them strengthen academic skills
                                and stay on track for college success.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <PublicFooter />
        </main>
    );
}

export default LandingPage;
